import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DollarSign, Plus, Save, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { EmptyState, PageHeader } from "../components/ui";
import { BilingualText } from "../components/ui/BilingualText";
import { api } from "../lib/api";
import type { CommissionStructureDTO } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

// M3X.COMP F.4 — commission structures editor.
// Card list per LE with inline create + per-row deactivate.

interface NewStructureForm {
  code: string;
  name_en: string;
  name_ar: string;
  type: string;
  cap_per_month: string;
  cap_per_year: string;
  payment_cadence: string;
  rate_rules_text: string;  // Multi-line input parsed as JSON
}

const EMPTY_NEW: NewStructureForm = {
  code: "",
  name_en: "",
  name_ar: "",
  type: "tiered_quota",
  cap_per_month: "",
  cap_per_year: "",
  payment_cadence: "monthly",
  rate_rules_text: '[{"threshold":"0","rate":"5"}]',
};

function StructureCard({
  structure,
  onDeactivated,
}: {
  structure: CommissionStructureDTO;
  onDeactivated: () => void;
}) {
  const { t } = useTranslation();
  const deactivate = useMutation({
    mutationFn: () => api.deleteCommissionStructure(structure.id),
    onSuccess: onDeactivated,
  });

  return (
    <div
      className={`rounded border p-4 ${
        structure.is_active
          ? "border-slate-200 bg-white"
          : "border-slate-200 bg-slate-50 opacity-60"
      }`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium">
              <BilingualText en={structure.name_en} ar={structure.name_ar} />
            </span>
            <span className="text-xs text-slate-500">{structure.code}</span>
            <span className="rounded bg-slate-100 px-2 py-0.5 text-xs uppercase">
              {structure.type}
            </span>
          </div>
          <div className="mt-1 text-xs text-slate-600">
            {t("commission.cadence")}: {structure.payment_cadence}
          </div>
        </div>
        {structure.is_active && (
          <button
            type="button"
            onClick={() => deactivate.mutate()}
            className="text-xs text-rose-600 hover:underline"
          >
            {t("commission.deactivate")}
          </button>
        )}
      </div>

      <div className="mt-2">
        <div className="text-xs font-medium text-slate-700">
          {t("commission.rate_rules")}
        </div>
        <table className="mt-1 w-full text-xs">
          <thead>
            <tr className="text-slate-500">
              <th className="text-left">{t("commission.threshold")}</th>
              <th className="text-left">{t("commission.rate")}</th>
            </tr>
          </thead>
          <tbody>
            {structure.rate_rules.map((r, i) => (
              <tr key={i}>
                <td>{r.threshold}</td>
                <td>{r.rate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(structure.cap_per_month || structure.cap_per_year) && (
        <div className="mt-2 text-xs text-slate-600">
          {structure.cap_per_month && (
            <div>
              {t("commission.cap_per_month")}: {structure.cap_per_month}{" "}
              {structure.currency}
            </div>
          )}
          {structure.cap_per_year && (
            <div>
              {t("commission.cap_per_year")}: {structure.cap_per_year}{" "}
              {structure.currency}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function CommissionStructuresPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState<NewStructureForm>(EMPTY_NEW);
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["commission-structures", legalEntityId],
    queryFn: () => api.listCommissionStructures(legalEntityId!),
    enabled: !!legalEntityId,
  });

  const create = useMutation({
    mutationFn: async () => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(newForm.rate_rules_text);
      } catch {
        throw new Error(t("commission.rate_rules_invalid_json"));
      }
      const body: Record<string, unknown> = {
        legal_entity_id: legalEntityId,
        code: newForm.code,
        name_en: newForm.name_en,
        name_ar: newForm.name_ar,
        type: newForm.type,
        payment_cadence: newForm.payment_cadence,
        rate_rules: parsed,
      };
      if (newForm.cap_per_month) body.cap_per_month = newForm.cap_per_month;
      if (newForm.cap_per_year) body.cap_per_year = newForm.cap_per_year;
      return api.createCommissionStructure(body);
    },
    onSuccess: () => {
      setError(null);
      setCreating(false);
      setNewForm(EMPTY_NEW);
      queryClient.invalidateQueries({
        queryKey: ["commission-structures", legalEntityId],
      });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (gate) return gate;
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorPanel message={(q.error as Error).message} />;

  const structures = q.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("commission.title")}
        subtitle={t("commission.subtitle")}
        actions={
          <button
            type="button"
            onClick={() => setCreating((v) => !v)}
            className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            {creating ? (
              <X className="h-4 w-4" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            {creating ? t("common.cancel") : t("commission.new")}
          </button>
        }
      />

      {creating && (
        <div className="space-y-3 rounded border border-slate-200 bg-white p-4">
          <div className="grid grid-cols-3 gap-3">
            <input
              placeholder={t("commission.code")}
              value={newForm.code}
              onChange={(e) =>
                setNewForm({ ...newForm, code: e.target.value })
              }
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            />
            <input
              placeholder={t("commission.name_en")}
              value={newForm.name_en}
              onChange={(e) =>
                setNewForm({ ...newForm, name_en: e.target.value })
              }
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            />
            <input
              placeholder={t("commission.name_ar")}
              value={newForm.name_ar}
              onChange={(e) =>
                setNewForm({ ...newForm, name_ar: e.target.value })
              }
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <select
              value={newForm.type}
              onChange={(e) =>
                setNewForm({ ...newForm, type: e.target.value })
              }
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            >
              <option value="tiered_quota">tiered_quota</option>
              <option value="percentage_of_revenue">percentage_of_revenue</option>
              <option value="flat_per_deal">flat_per_deal</option>
              <option value="hybrid">hybrid</option>
            </select>
            <select
              value={newForm.payment_cadence}
              onChange={(e) =>
                setNewForm({ ...newForm, payment_cadence: e.target.value })
              }
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            >
              <option value="monthly">monthly</option>
              <option value="quarterly">quarterly</option>
              <option value="upon_collection">upon_collection</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <input
              type="number"
              min={0}
              step="0.01"
              placeholder={t("commission.cap_per_month")}
              value={newForm.cap_per_month}
              onChange={(e) =>
                setNewForm({ ...newForm, cap_per_month: e.target.value })
              }
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            />
            <input
              type="number"
              min={0}
              step="0.01"
              placeholder={t("commission.cap_per_year")}
              value={newForm.cap_per_year}
              onChange={(e) =>
                setNewForm({ ...newForm, cap_per_year: e.target.value })
              }
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </div>

          <label className="block text-xs">
            <span className="text-slate-700">{t("commission.rate_rules")}</span>
            <textarea
              value={newForm.rate_rules_text}
              onChange={(e) =>
                setNewForm({ ...newForm, rate_rules_text: e.target.value })
              }
              rows={4}
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
            />
          </label>

          {error && (
            <div className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={create.isPending}
            className="inline-flex items-center gap-2 rounded bg-brand-deep px-3 py-2 text-sm text-white hover:bg-slate-900 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {create.isPending ? t("common.saving") : t("common.save")}
          </button>
        </div>
      )}

      {structures.length === 0 ? (
        <EmptyState
          icon={DollarSign}
          headline={t("commission.empty_title")}
          description={t("commission.empty_desc")}
        />
      ) : (
        <div className="space-y-3">
          {structures.map((s) => (
            <StructureCard
              key={s.id}
              structure={s}
              onDeactivated={() =>
                queryClient.invalidateQueries({
                  queryKey: ["commission-structures", legalEntityId],
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
